import { useTranslation } from 'react-i18next';
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { FileInputIcon } from 'lucide-react';
import { IqAnalysisWidgetKind } from '@inference.engine/widgets/web';

import { saveAnalysisAsArtifact } from '@/features/analysis';
import { BriefStartFlow } from '@/features/brief-generation';

interface AnalysisStartBriefButtonProps {
  analysisId: string;
  /** Workspace to mint the artifact in; the section layout owns the gating that resolves it. */
  workspaceId: string;
  /** Brand the analysis is about, used to label the attached artifact row. */
  brand?: string;
}

/**
 * Starts a brief in a chosen project with this analysis already attached as
 * context, so the generation agent reads the report instead of the user having
 * to restate it.
 *
 * The attachment reuses the same artifact "Add to project" mints (keyed by the
 * analysis id, so both entry points converge on one row). It is a workspace
 * artifact, which is exactly what brief intake accepts, so it needs no project
 * membership of its own.
 */
export const AnalysisStartBriefButton = ({
  analysisId,
  workspaceId,
  brand,
}: AnalysisStartBriefButtonProps) => {
  const { t } = useTranslation('analysis');
  const label = t('analysisResult.analysisStartBriefButton.startBrief');

  const prepareArtifacts = async () => {
    const artifactId = await saveAnalysisAsArtifact(analysisId, workspaceId);
    return [
      {
        id: artifactId,
        name: brand?.trim() || t('analysisResult.analysisStartBriefButton.artifactFallbackName'),
        kind: IqAnalysisWidgetKind,
      },
    ];
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <BriefStartFlow prepareArtifacts={prepareArtifacts}>
          {/* Same asChild composition as the Add to project trigger: the tooltip
              hangs on the button, not on the picker it opens. */}
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon-sm" aria-label={label}>
              <FileInputIcon className="size-4" />
            </Button>
          </TooltipTrigger>
        </BriefStartFlow>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
